<template>
  <transition
    enter-active-class="transform ease-out duration-300 transition"
    enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:-translate-x-2"
    enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
    leave-active-class="transition ease-in duration-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
    @after-leave="emit('close', props.id)"
  >
    <div
      v-if="show"
      role="alert"
      :class="[
        'pointer-events-auto w-full max-w-xs overflow-hidden rounded-lg bg-white dark:bg-gray-900 ring-1 ring-black/15 dark:ring-white/15',
        depthClass,
        onClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors' : ''
      ]"
      @click="handleClick"
    >
      <div class="p-2.5">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <component
              :is="iconComponent"
              :class="iconClasses"
              aria-hidden="true"
            />
          </div>
          <div class="ml-2 w-0 flex-1 pt-0.5">
            <p class="text-sm font-medium leading-tight text-gray-900 dark:text-white">
              {{ title }}
              <span
                v-if="count > 1"
                :aria-label="`${count} occurrences`"
                class="ml-1 inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 text-3xs font-semibold text-gray-700 dark:text-gray-200"
              >×{{ count }}</span>
            </p>
            <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {{ message }}
            </p>
            <div v-if="actions && actions.length" class="mt-1.5 flex space-x-4">
              <button
                v-for="action in actions"
                :key="action.actionId || action.label"
                type="button"
                class="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300"
                :aria-label="`${action.label} \u2014 ${title}`"
                @click.stop="handleAction(action)"
              >
                {{ action.label }}
              </button>
            </div>
          </div>
          <div class="ml-2 flex flex-shrink-0">
            <button
              type="button"
              @click.stop="show = false"
              class="inline-flex rounded-md bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-400"
            >
              <span class="sr-only">Close</span>
              <XMarkIcon class="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, computed, watch, onBeforeUnmount } from "vue";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ExclamationCircleIcon
} from "@heroicons/vue/24/outline";
import { XMarkIcon } from "@heroicons/vue/20/solid";

const props = defineProps({
  id: {
    type: String,
    required: true
  },
  type: {
    type: String,
    default: 'info',
    validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
  },
  title: {
    type: String,
    required: true
  },
  message: {
    type: String,
    required: true
  },
  autoHideSeconds: {
    type: Number,
    default: null
  },
  icon: {
    type: String,
    default: null
  },
  iconColor: {
    type: String,
    default: null
  },
  onClick: {
    type: Function,
    default: null
  },
  actions: {
    type: Array,
    default: null
  },
  // Coalesce count (bead th-5c153). Rendered as a "×N" pill next to the title
  // when > 1. The store mutates this on subsequent addNotification calls with
  // a matching groupKey.
  count: {
    type: Number,
    default: 1
  },
  // Monotonically incremented by the store on each coalesce-merge so this
  // component can re-arm its auto-hide timer (bead th-5c153). Without this,
  // a coalesced toast inherits the first call's countdown and disappears
  // even though new occurrences keep arriving.
  bumpSignal: {
    type: Number,
    default: 0
  },
  // The toast's shadow, as the class its host's overlay band names
  // (`useOverlay().depthClass` in NotificationsOverlay). A toast floats in
  // that host's band, so it spells no shadow of its own.
  depthClass: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['close', 'action']);

const show = ref(true);

function handleClick() {
  if (props.onClick) {
    props.onClick();
    show.value = false;
  }
}

function handleAction(action) {
  if (action.handler) {
    action.handler();
  }
  emit('action', action.actionId);
  show.value = false;
}

const notificationConfig = {
  success: {
    icon: CheckCircleIcon,
    color: 'text-emerald-400'
  },
  error: {
    icon: ExclamationTriangleIcon,
    color: 'text-red-400'
  },
  warning: {
    icon: ExclamationTriangleIcon,
    color: 'text-amber-400'
  },
  info: {
    icon: InformationCircleIcon,
    color: 'text-blue-400'
  }
};

const iconComponent = computed(() => {
  if (props.icon) {
    const iconMap = {
      'CheckCircleIcon': CheckCircleIcon,
      'ExclamationTriangleIcon': ExclamationTriangleIcon,
      'InformationCircleIcon': InformationCircleIcon,
      'ExclamationCircleIcon': ExclamationCircleIcon
    };
    return iconMap[props.icon] || notificationConfig[props.type].icon;
  }
  return notificationConfig[props.type].icon;
});

const iconClasses = computed(() => {
  const baseClasses = 'h-5 w-5';
  const color = props.iconColor || notificationConfig[props.type].color;
  return `${baseClasses} ${color}`;
});

let hideTimer = null;

function armHideTimer() {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  if (props.autoHideSeconds) {
    hideTimer = setTimeout(() => {
      show.value = false;
    }, props.autoHideSeconds * 1000);
  }
}

onMounted(armHideTimer);

// Re-arm the timer whenever the store coalesces a new occurrence into this
// toast (props.bumpSignal increments). Without this, the toast inherits the
// original mount-time deadline and disappears mid-flood.
watch(
  () => props.bumpSignal,
  (next, prev) => {
    if (next !== prev) armHideTimer();
  },
);

onBeforeUnmount(() => {
  if (hideTimer) clearTimeout(hideTimer);
});
</script>
